/**
 * PortfolioInboxView
 *
 * Release inbox of the Brand Portfolio, across every brand the viewer can reach:
 * - "Awaiting my release" (staff reviewers): items waiting for the viewer's release. Items whose
 *   requested changes were made are pinned first with a marker.
 * - "Changes requested": items a reviewer sent back, for the operators to rework.
 * A row opens the brand (God Mode) and the Release drawer for the item through the existing
 * portfolio deep link (see AgencyPortfolio's `openBrand` handling).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BrandTabPanel, BrandTabs, type BrandTabItem } from '@/components/ui/BrandTabs';
import { Icon } from '@/components/ui/Icon';
import { RetryImage } from '@/components/ui/RetryImage';
import { TextPostTile } from '@/components/ui/TextPostTile';
import { NotificationBrandChip } from '@/components/notifications/NotificationBrandChip';
import { NEEDED_BY_CHIP_CLASS, getNeededByInfo } from '@/components/release/neededBy';
import { ICONS } from '@/constants/icons';
import { useAuth } from '@/hooks/useAuth';
import { useReleaseInbox, useReleaseInboxCounts } from '@/hooks/useReleaseSections';
import { useTimezone } from '@/contexts/TimezoneContext';
import type { PortfolioClient } from '@/data/portfolio.types';
import { buildPortfolioReleaseHref } from '@/utils/releaseLinks';
import { cn } from '@/utils/cn';
import { languageBadgeCode } from '@/utils/languages';
import type { SectionItem } from '@/types/releaseSections';

type InboxTab = 'awaiting' | 'changes_requested';

const TAB_ID_PREFIX = 'release-inbox';

export interface PortfolioInboxViewProps {
  /** Already-filtered client list from the parent page */
  clients: PortfolioClient[];
  /** Size of the unfiltered portfolio (a single remaining brand narrows the inbox to it) */
  totalClientCount: number;
}

// ── Row ──────────────────────────────────────────────────────────────────────

interface InboxRowProps {
  item: SectionItem;
  tab: InboxTab;
  pinned: boolean;
}

const InboxRow = ({ item, tab, pinned }: InboxRowProps) => {
  const { t } = useTranslation('agency');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { timezone, formatRelativeDate } = useTimezone();

  const { post, variation } = item;
  const mediaUrls = variation?.mediaUrls?.length ? variation.mediaUrls : post.mediaUrls;
  const label = item.label.trim() || tc('untitled_post');
  const versionName = item.version.isOriginal
    ? null
    : item.version.label?.trim() || languageBadgeCode(item.version.language);
  const needed = getNeededByInfo(item.neededBy, timezone, tc);

  const openItem = (): void => {
    navigate(
      buildPortfolioReleaseHref({
        teamId: item.teamId,
        postId: item.postId,
        variationId: item.variationId,
      })
    );
  };

  const when = tab === 'changes_requested' && item.decision
    ? formatRelativeDate(item.decision.decidedAt)
    : formatRelativeDate(item.approval.requestedAt);
  const meta =
    tab === 'changes_requested' && item.decision
      ? t('release_inbox_decision_by', { name: item.decision.byName, when })
      : t('release_inbox_sent_by', { name: item.sentBy.name, when });

  return (
    <button
      type="button"
      onClick={openItem}
      className="group flex w-full items-start gap-3 rounded-xl border border-gray-900/[0.08] bg-white p-3 text-left transition-all hover:border-primary-200 hover:shadow-sm"
    >
      <span className="block h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
        {mediaUrls.length > 0 ? (
          <RetryImage
            src={mediaUrls[0]}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <TextPostTile text={post.content || variation?.title} emptyLabel={tc('approval_no_content')} />
        )}
      </span>

      <span className="block min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-1.5">
          <NotificationBrandChip name={item.brand.name} logo={item.brand.logo} />
          {pinned && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-semibold text-primary-700">
              <Icon name={ICONS.REFRESH} size="xs" ariaHidden />
              {t('release_inbox_changes_made_marker')}
            </span>
          )}
          {versionName && (
            <span className="inline-flex items-center gap-0.5 rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-800">
              <Icon name={ICONS.LANGUAGE} size="xs" ariaHidden />
              {versionName}
            </span>
          )}
        </span>
        <span className="mt-1 block truncate text-sm font-semibold text-gray-900">{label}</span>
        <span className="mt-0.5 block truncate text-xs text-gray-500">{meta}</span>
        {tab === 'changes_requested' && item.decision?.commentExcerpt && (
          <span className="mt-1 line-clamp-2 block break-words text-xs italic text-gray-600">
            &ldquo;{item.decision.commentExcerpt}&rdquo;
          </span>
        )}
      </span>

      {needed && (
        <span
          className={cn(
            'flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold',
            NEEDED_BY_CHIP_CLASS[needed.tone]
          )}
        >
          {needed.label}
        </span>
      )}
    </button>
  );
};

// ── Lists ────────────────────────────────────────────────────────────────────

const RowsSkeleton = () => (
  <div className="space-y-2" aria-hidden="true">
    {Array.from({ length: 4 }).map((_, index) => (
      <div key={index} className="h-[78px] animate-pulse rounded-xl border border-gray-900/[0.06] bg-gray-50" />
    ))}
  </div>
);

interface InboxStateProps {
  emptyText: string;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  onRetry: () => void;
}

/** Loading, error (manual retry) and empty states shared by both tabs. Null when there are rows. */
const InboxState = ({ emptyText, isLoading, isError, isEmpty, onRetry }: InboxStateProps) => {
  const { t } = useTranslation('agency');
  if (isLoading) return <RowsSkeleton />;
  if (isError) {
    return (
      <div className="flex flex-wrap items-center justify-center gap-3 py-12 text-sm text-gray-600">
        <span>{t('release_inbox_error')}</span>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          {t('release_inbox_retry')}
        </button>
      </div>
    );
  }
  if (isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-primary-50">
          <Icon name={ICONS.CHECK_CIRCLE} size="lg" color="text-primary-500" ariaHidden />
        </div>
        <p className="max-w-xs text-sm text-gray-600">{emptyText}</p>
      </div>
    );
  }
  return null;
};

interface LoadMoreProps {
  visible: boolean;
  isLoading: boolean;
  onClick: () => void;
}

const LoadMore = ({ visible, isLoading, onClick }: LoadMoreProps) => {
  const { t } = useTranslation('agency');
  if (!visible) return null;
  return (
    <div className="flex justify-center pt-2">
      <button
        type="button"
        onClick={onClick}
        disabled={isLoading}
        className="rounded-lg bg-primary-50 px-4 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-100 disabled:opacity-50"
      >
        {t('release_inbox_load_more')}
      </button>
    </div>
  );
};

interface TabListProps {
  teamFilter: number | null;
}

const AwaitingList = ({ teamFilter }: TabListProps) => {
  const { t } = useTranslation('agency');
  const pinnedQuery = useReleaseInbox('changes_made', teamFilter, true);
  const awaitingQuery = useReleaseInbox('awaiting', teamFilter, true);

  // Changes-made rows lead; the same item never appears twice.
  const rows = useMemo(() => {
    const pinnedKeys = new Set(pinnedQuery.items.map((item) => `${item.teamId}:${item.id}`));
    const rest = awaitingQuery.items.filter((item) => !pinnedKeys.has(`${item.teamId}:${item.id}`));
    return [
      ...pinnedQuery.items.map((item) => ({ item, pinned: true })),
      ...rest.map((item) => ({ item, pinned: false })),
    ];
  }, [pinnedQuery.items, awaitingQuery.items]);

  const isLoading = pinnedQuery.isLoading || awaitingQuery.isLoading;
  const isError = pinnedQuery.isError || awaitingQuery.isError;
  const hasNext = pinnedQuery.hasNextPage || awaitingQuery.hasNextPage;
  const isFetchingNext = pinnedQuery.isFetchingNextPage || awaitingQuery.isFetchingNextPage;

  return (
    <div className="space-y-2">
      <InboxState
        emptyText={t('release_inbox_empty_awaiting')}
        isLoading={isLoading}
        isError={isError}
        isEmpty={rows.length === 0}
        onRetry={() => {
          pinnedQuery.refetch();
          awaitingQuery.refetch();
        }}
      />
      {!isLoading &&
        rows.map(({ item, pinned }) => (
          <InboxRow key={`${item.teamId}:${item.id}`} item={item} tab="awaiting" pinned={pinned} />
        ))}
      <LoadMore
        visible={!isLoading && hasNext}
        isLoading={isFetchingNext}
        onClick={() => {
          if (pinnedQuery.hasNextPage) pinnedQuery.fetchNextPage();
          if (awaitingQuery.hasNextPage) awaitingQuery.fetchNextPage();
        }}
      />
    </div>
  );
};

const ChangesRequestedList = ({ teamFilter }: TabListProps) => {
  const { t } = useTranslation('agency');
  const query = useReleaseInbox('changes_requested', teamFilter, true);
  return (
    <div className="space-y-2">
      <InboxState
        emptyText={t('release_inbox_empty_changes')}
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={query.items.length === 0}
        onRetry={query.refetch}
      />
      {!query.isLoading &&
        query.items.map((item) => (
          <InboxRow key={`${item.teamId}:${item.id}`} item={item} tab="changes_requested" pinned={false} />
        ))}
      <LoadMore
        visible={!query.isLoading && query.hasNextPage}
        isLoading={query.isFetchingNextPage}
        onClick={query.fetchNextPage}
      />
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

const CountPill = ({ count }: { count: number | undefined }) =>
  count !== undefined && count > 0 ? (
    <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-gray-700">
      {count}
    </span>
  ) : null;

export const PortfolioInboxView = ({ clients, totalClientCount }: PortfolioInboxViewProps) => {
  const { t } = useTranslation('agency');
  const { user } = useAuth();
  const { data: counts } = useReleaseInboxCounts(true);

  // The brand filter narrowed the portfolio to one brand: the inbox follows it.
  const onlyClient = clients.length === 1 ? clients[0] : undefined;
  const teamFilter = onlyClient && totalClientCount > 1 ? onlyClient.id : null;

  // "Awaiting my release" is for staff reviewers; it is the default tab when it exists.
  const showAwaiting = user?.agencyIsReviewer === true || (counts?.awaiting ?? 0) > 0;
  const [chosenTab, setChosenTab] = useState<InboxTab | null>(null);
  const tab: InboxTab = chosenTab && (chosenTab !== 'awaiting' || showAwaiting)
    ? chosenTab
    : showAwaiting
      ? 'awaiting'
      : 'changes_requested';

  const tabs: BrandTabItem<InboxTab>[] = [
    ...(showAwaiting
      ? [
          {
            id: 'awaiting' as const,
            label: (
              <>
                {t('release_inbox_tab_awaiting')}
                <CountPill count={counts?.awaiting} />
              </>
            ),
          },
        ]
      : []),
    {
      id: 'changes_requested' as const,
      label: (
        <>
          {t('release_inbox_tab_changes_requested')}
          <CountPill count={counts?.changesRequested} />
        </>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <BrandTabs
        tabs={tabs}
        value={tab}
        onChange={setChosenTab}
        ariaLabel={t('release_inbox_tabs_aria')}
        idPrefix={TAB_ID_PREFIX}
        variant="underline"
      />
      <BrandTabPanel idPrefix={TAB_ID_PREFIX} id={tab}>
        {tab === 'awaiting' ? (
          <AwaitingList teamFilter={teamFilter} />
        ) : (
          <ChangesRequestedList teamFilter={teamFilter} />
        )}
      </BrandTabPanel>
    </div>
  );
};
